import React, { useCallback, useMemo } from 'react';

import { DateFormatter } from '~/@components/Formatters';
import { LocalizableText } from '~/@components/LocalizableText';
import { ActionVariant, ITableCellAction, TableCellActions } from '~/@components/Table/TableCellActions';
import { deleteTimeSpentFromProject } from '~/@user-store/timeSpent/timeSpent.requests';
import { Project, ProjectTimeSpentRecord } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/types';
import { useUserAbilities } from '~/contexts';
import { showConfirmDialog } from '~/services/dialog';
import { canDeleteTimeSpent, canUpdateTimeSpent } from '~/utils/auth';

type Props = {
    timeSpent: ProjectTimeSpentRecord;
    project: Project;
    onEdit: () => void;
};

export const ProjectTimeSpentOrderDateCell = ({ timeSpent, onEdit, project }: Props) => {
    const ability = useUserAbilities();

    const canEdit = useMemo(() => canUpdateTimeSpent(ability, project, timeSpent), [ability, timeSpent, project]);

    const canDelete = useMemo(() => canDeleteTimeSpent(ability, project, timeSpent), [ability, timeSpent, project]);

    const onDelete = useCallback(async () => {
        const confirmed = await showConfirmDialog({
            title: <LocalizableText code={'ProjectDocuments.timeSpent.deleteInfo'} />,
            message: <LocalizableText code={'ProjectDocuments.timeSpent.deleteConfirmation'} />,
        });

        if (!confirmed) return;

        await deleteTimeSpentFromProject(project.id, timeSpent.id);
    }, [project, timeSpent]);

    const actions: Array<ITableCellAction> = useMemo(
        () => [
            {
                variant: ActionVariant.EDIT,
                onClick: onEdit,
                disabled: !canEdit,
            },
            {
                variant: ActionVariant.DELETE,
                onClick: onDelete,
                disabled: !canDelete || !canEdit,
            },
        ],
        [onEdit, canEdit, canDelete, onDelete]
    );

    return (
        <TableCellActions actions={actions}>
            <DateFormatter>{timeSpent.date}</DateFormatter>
        </TableCellActions>
    );
};
